import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
    UserCog,
    Plus,
    Search,
    MoreHorizontal,
    Pencil,
    Trash2,
    Loader2,
    Mail,
    Calculator,
    Euro,
    Percent,
    Settings2,
    X,
    Copy,
    AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const AdminUsers = () => {
    const { api, user: currentUser } = useAuth();
    
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [defaultBrackets, setDefaultBrackets] = useState([]);
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCommissionModal, setShowCommissionModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'commercial',
        is_active: true,
        has_variable_compensation: true,
    });
    
    // Commission brackets form
    const [customBrackets, setCustomBrackets] = useState([]);
    const [useCustomBrackets, setUseCustomBrackets] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersRes, bracketsRes] = await Promise.all([
                api.get('/users'),
                api.get('/commissions/default-brackets'),
            ]);
            setUsers(usersRes.data);
            setDefaultBrackets(bracketsRes.data.brackets || []);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await api.post('/users', formData);
            toast.success('Utilisateur créé');
            setShowCreateModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Failed to create user:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            const updateData = { ...formData };
            if (!updateData.password) {
                delete updateData.password;
            }
            
            await api.patch(`/users/${selectedUser.id}`, updateData);
            toast.success('Utilisateur mis à jour');
            setShowEditModal(false);
            fetchData();
        } catch (error) {
            console.error('Failed to update user:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        
        try {
            await api.delete(`/users/${selectedUser.id}`);
            toast.success('Utilisateur supprimé');
            setShowDeleteModal(false);
            fetchData();
        } catch (error) {
            console.error('Failed to delete user:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveCommissionBrackets = async () => {
        setSubmitting(true);
        
        try {
            // Validate brackets
            if (useCustomBrackets && customBrackets.length > 0) {
                // Sort by min_amount
                const sortedBrackets = [...customBrackets].sort((a, b) => a.min_amount - b.min_amount);
                
                // Check for gaps or overlaps
                for (let i = 0; i < sortedBrackets.length - 1; i++) {
                    const current = sortedBrackets[i];
                    const next = sortedBrackets[i + 1];
                    if (current.max_amount !== next.min_amount) {
                        toast.error(`Les tranches doivent être continues. Vérifiez la tranche ${i + 1}`);
                        setSubmitting(false);
                        return;
                    }
                }
            }
            
            const updateData = {
                custom_commission_brackets: useCustomBrackets ? customBrackets : null
            };
            
            await api.patch(`/users/${selectedUser.id}`, updateData);
            toast.success('Barème personnalisé enregistré');
            setShowCommissionModal(false);
            fetchData();
        } catch (error) {
            console.error('Failed to save commission brackets:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            password: '',
            role: 'commercial',
            is_active: true,
            has_variable_compensation: true,
        });
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            password: '',
            role: user.role,
            is_active: user.is_active,
            has_variable_compensation: user.has_variable_compensation !== false,
        });
        setShowEditModal(true);
    };

    const openCommissionModal = (user) => {
        setSelectedUser(user);
        
        if (user.custom_commission_brackets && user.custom_commission_brackets.length > 0) {
            setCustomBrackets(user.custom_commission_brackets.map(b => ({
                min_amount: b.min_amount || b.min || 0,
                max_amount: b.max_amount || b.max || null,
                rate: b.rate || 0,
            })));
            setUseCustomBrackets(true);
        } else {
            // Start with default brackets
            setCustomBrackets(defaultBrackets.map(b => ({
                min_amount: b.min || 0,
                max_amount: b.max || null,
                rate: b.rate || 0,
            })));
            setUseCustomBrackets(false);
        }
        
        setShowCommissionModal(true);
    };

    const addBracket = () => {
        const lastBracket = customBrackets[customBrackets.length - 1];
        const newMin = lastBracket ? (lastBracket.max_amount || lastBracket.min_amount + 5000) : 0;
        setCustomBrackets([
            ...customBrackets,
            { min_amount: newMin, max_amount: newMin + 5000, rate: 0.10 }
        ]);
    };

    const updateBracket = (index, field, value) => {
        const newBrackets = [...customBrackets];
        newBrackets[index] = { ...newBrackets[index], [field]: value };
        
        // Auto-adjust next bracket's min if we changed max
        if (field === 'max_amount' && index < customBrackets.length - 1) {
            newBrackets[index + 1] = { ...newBrackets[index + 1], min_amount: value };
        }
        
        setCustomBrackets(newBrackets);
    };

    const removeBracket = (index) => {
        if (customBrackets.length <= 1) {
            toast.error('Au moins une tranche est requise');
            return;
        }
        const newBrackets = customBrackets.filter((_, i) => i !== index);
        // Adjust the next bracket's min to match previous bracket's max
        if (index > 0 && index < newBrackets.length) {
            newBrackets[index] = { 
                ...newBrackets[index], 
                min_amount: newBrackets[index - 1].max_amount || 0 
            };
        }
        setCustomBrackets(newBrackets);
    };

    const copyDefaultBrackets = () => {
        setCustomBrackets(defaultBrackets.map(b => ({
            min_amount: b.min || 0,
            max_amount: b.max || null,
            rate: b.rate || 0,
        })));
        toast.success('Barème par défaut copié');
    };

    const getRoleBadge = (role) => {
        const styles = {
            super_admin: 'bg-purple-500/10 text-purple-500',
            assistant: 'bg-blue-500/10 text-blue-500',
            commercial: 'bg-green-500/10 text-green-500',
            editorial: 'bg-orange-500/10 text-orange-500',
        };
        const labels = {
            super_admin: 'Super Admin',
            assistant: 'Assistant',
            commercial: 'Commercial',
            editorial: 'Éditorial',
        };
        return (
            <Badge className={styles[role] || styles.commercial}>
                {labels[role] || role}
            </Badge>
        );
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '∞';
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
    };

    const filteredUsers = users.filter(u => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return u.first_name.toLowerCase().includes(searchLower) ||
               u.last_name.toLowerCase().includes(searchLower) ||
               u.email.toLowerCase().includes(searchLower);
    });

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-users-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading">Utilisateurs</h1>
                    <p className="text-muted-foreground mt-1">
                        Gérez les comptes utilisateurs et leurs rôles
                    </p>
                </div>
                <Button
                    onClick={() => {
                        resetForm();
                        setShowCreateModal(true);
                    }}
                    className="rounded-full"
                    data-testid="create-user-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvel utilisateur
                </Button>
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher par nom ou email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 rounded-lg"
                            data-testid="search-users-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {filteredUsers.length === 0 ? (
                        <div className="text-center py-12">
                            <UserCog className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Utilisateur</TableHead>
                                    <TableHead className="hidden md:table-cell">Email</TableHead>
                                    <TableHead>Rôle</TableHead>
                                    <TableHead>Variable</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.map((user) => (
                                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Avatar>
                                                    <AvatarFallback className="bg-primary/10 text-primary">
                                                        {getInitials(user.first_name, user.last_name)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="font-medium">{user.first_name} {user.last_name}</p>
                                                    <p className="text-sm text-muted-foreground md:hidden">{user.email}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell">
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                <Mail className="w-4 h-4" />
                                                {user.email}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {getRoleBadge(user.role)}
                                        </TableCell>
                                        <TableCell>
                                            {user.role === 'commercial' ? (
                                                user.has_variable_compensation !== false ? (
                                                    <div className="flex items-center gap-1">
                                                        <Badge className={user.custom_commission_brackets ? 'bg-purple-500/10 text-purple-500' : 'bg-blue-500/10 text-blue-500'}>
                                                            {user.custom_commission_brackets ? 'Personnalisé' : 'Standard'}
                                                        </Badge>
                                                    </div>
                                                ) : (
                                                    <Badge className="bg-gray-500/10 text-gray-500">
                                                        Aucun
                                                    </Badge>
                                                )
                                            ) : (
                                                <span className="text-muted-foreground text-sm">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={user.is_active ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}>
                                                {user.is_active ? 'Actif' : 'Inactif'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="rounded-full">
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => openEditModal(user)}>
                                                        <Pencil className="w-4 h-4 mr-2" />
                                                        Modifier
                                                    </DropdownMenuItem>
                                                    {user.role === 'commercial' && user.has_variable_compensation !== false && (
                                                        <DropdownMenuItem onClick={() => openCommissionModal(user)}>
                                                            <Calculator className="w-4 h-4 mr-2" />
                                                            Barème commissions
                                                        </DropdownMenuItem>
                                                    )}
                                                    {user.id !== currentUser.id && (
                                                        <>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem
                                                                onClick={() => {
                                                                    setSelectedUser(user);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                                className="text-destructive focus:text-destructive"
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Supprimer
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                }
            }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {showEditModal ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
                        </DialogTitle>
                        <DialogDescription>
                            {showEditModal ? 'Mettez à jour les informations' : 'Créez un nouveau compte utilisateur'}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Prénom *</Label>
                                <Input
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    required
                                    className="rounded-lg"
                                    data-testid="user-firstname-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nom *</Label>
                                <Input
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    required
                                    className="rounded-lg"
                                    data-testid="user-lastname-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                className="rounded-lg"
                                data-testid="user-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{showEditModal ? 'Nouveau mot de passe' : 'Mot de passe *'}</Label>
                            <Input
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                required={!showEditModal}
                                placeholder={showEditModal ? 'Laisser vide pour ne pas changer' : ''}
                                className="rounded-lg"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Rôle *</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(v) => setFormData({ ...formData, role: v })}
                                >
                                    <SelectTrigger className="rounded-lg" data-testid="user-role-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="commercial">Commercial</SelectItem>
                                        <SelectItem value="assistant">Assistant</SelectItem>
                                        <SelectItem value="editorial">Éditorial</SelectItem>
                                        <SelectItem value="super_admin">Super Admin</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Statut</Label>
                                <Select
                                    value={formData.is_active ? 'active' : 'inactive'}
                                    onValueChange={(v) => setFormData({ ...formData, is_active: v === 'active' })}
                                >
                                    <SelectTrigger className="rounded-lg">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">Actif</SelectItem>
                                        <SelectItem value="inactive">Inactif</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Variable compensation option (only for commercial role) */}
                        {formData.role === 'commercial' && (
                            <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                                <div className="space-y-0.5">
                                    <Label className="text-base">Rémunération variable</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Ce commercial a-t-il une part variable dans son contrat ?
                                    </p>
                                </div>
                                <Switch
                                    checked={formData.has_variable_compensation}
                                    onCheckedChange={(checked) => setFormData({ ...formData, has_variable_compensation: checked })}
                                    data-testid="has-variable-switch"
                                />
                            </div>
                        )}

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="user-submit-btn">
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {showEditModal ? 'Mise à jour...' : 'Création...'}
                                    </>
                                ) : (
                                    showEditModal ? 'Mettre à jour' : 'Créer'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Commission Brackets Modal */}
            <Dialog open={showCommissionModal} onOpenChange={setShowCommissionModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <Calculator className="w-5 h-5" />
                            Barème de commissions - {selectedUser?.first_name} {selectedUser?.last_name}
                        </DialogTitle>
                        <DialogDescription>
                            Personnalisez le barème de commissions pour ce commercial
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* Toggle custom brackets */}
                        <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                            <div className="space-y-0.5">
                                <Label className="text-base">Barème personnalisé</Label>
                                <p className="text-sm text-muted-foreground">
                                    Utiliser un barème différent du barème standard
                                </p>
                            </div>
                            <Switch
                                checked={useCustomBrackets}
                                onCheckedChange={setUseCustomBrackets}
                                data-testid="use-custom-brackets-switch"
                            />
                        </div>

                        {useCustomBrackets && (
                            <>
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium">Tranches de commission</h4>
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={copyDefaultBrackets}
                                            className="rounded-full"
                                        >
                                            <Copy className="w-4 h-4 mr-1" />
                                            Copier barème standard
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={addBracket}
                                            className="rounded-full"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Ajouter
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    {customBrackets.map((bracket, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 rounded-lg border bg-background">
                                            <div className="flex-1 grid grid-cols-3 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Seuil (€)</Label>
                                                    <div className="relative">
                                                        <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                                                        <Input
                                                            type="number"
                                                            value={bracket.min_amount}
                                                            onChange={(e) => updateBracket(index, 'min_amount', parseFloat(e.target.value) || 0)}
                                                            className="pl-7 h-9 rounded-lg"
                                                            disabled={index > 0}
                                                            data-testid={`bracket-${index}-min`}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Plafond (€)</Label>
                                                    <div className="relative">
                                                        <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                                                        <Input
                                                            type="number"
                                                            value={bracket.max_amount || ''}
                                                            onChange={(e) => updateBracket(index, 'max_amount', e.target.value ? parseFloat(e.target.value) : null)}
                                                            placeholder="∞"
                                                            className="pl-7 h-9 rounded-lg"
                                                            data-testid={`bracket-${index}-max`}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Taux (%)</Label>
                                                    <div className="relative">
                                                        <Percent className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                                                        <Input
                                                            type="number"
                                                            step="0.1"
                                                            min="0"
                                                            max="100"
                                                            value={(bracket.rate * 100).toFixed(1)}
                                                            onChange={(e) => updateBracket(index, 'rate', (parseFloat(e.target.value) || 0) / 100)}
                                                            className="pl-7 h-9 rounded-lg"
                                                            data-testid={`bracket-${index}-rate`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeBracket(index)}
                                                className="h-9 w-9 text-destructive hover:text-destructive"
                                                disabled={customBrackets.length <= 1}
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>

                                {/* Preview */}
                                <div className="p-4 rounded-lg bg-muted/50">
                                    <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        Aperçu du barème
                                    </h5>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="text-xs">Tranche</TableHead>
                                                <TableHead className="text-xs text-right">Taux</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {customBrackets.map((b, i) => (
                                                <TableRow key={i}>
                                                    <TableCell className="text-sm">
                                                        {formatCurrency(b.min_amount)} - {formatCurrency(b.max_amount)}
                                                    </TableCell>
                                                    <TableCell className="text-sm text-right font-medium">
                                                        {(b.rate * 100).toFixed(1)}%
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </>
                        )}

                        {!useCustomBrackets && (
                            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                                <p className="text-sm text-muted-foreground">
                                    Ce commercial utilisera le <strong>barème standard</strong> défini pour l'entreprise.
                                </p>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowCommissionModal(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSaveCommissionBrackets}
                            disabled={submitting}
                            className="rounded-full"
                            data-testid="save-brackets-btn"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Enregistrement...
                                </>
                            ) : (
                                'Enregistrer'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Confirmer la suppression</DialogTitle>
                        <DialogDescription>
                            Êtes-vous sûr de vouloir supprimer l'utilisateur "{selectedUser?.first_name} {selectedUser?.last_name}" ?
                            Cette action est irréversible.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteModal(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={submitting}
                            className="rounded-full"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminUsers;
